/**
 * 资料清单「行内直接编辑」的纯函数(0925 B7):哪一行能改、用什么输入框、输入怎么变成 api 答案、图片行传到哪。
 *
 * ★ 保存走现成的 `PUT collect/answers`(附录 A.30 形状),不开新端点:
 *   `{questionId: "row:{path}", fieldPath: path, value, confirm: true}` → api 写 `FieldState(value, "user:row:{path}",
 *   「你填写的」, confirmed)`(`WebsiteAnswerService.applyField`,题目不在追问列表里也照落)—— 即 `user:{qid}` 绑定,
 *   A.1 / A.35② 的出处语义不变。pattern 路径(`cases[].client`,一条都没有时)api 按 A.17 写到第一项。
 * ★ 图片行复用 `POST sites/{id}/assets/image`(A.53):只在 host 模式可用。Logo / 首屏图 / 产品图按各自 purpose;
 *   W15(1013):案例配图、资质证书以前「没有对应 purpose」而禁用 —— 1010 起 api 已收 `gallery`(客户自有图转存,
 *   不绑位置,只回 url),这两类改走 gallery,url 由本行保存写进资料,api 不用再扩 purpose。
 */
import type {
  ChecklistItem,
  CollectAnswer,
  FieldSpec,
  FieldType,
  UploadImageRequest,
} from "../api/types";
import { isCollectableRow } from "./collect";

/** 行内编辑答案的题号前缀(api 出处 = `user:row:{path}`)。 */
export const ROW_EDIT_PREFIX = "row:";

export type RowEditorKind = "text" | "textarea" | "list" | "enum" | "image" | "asset_list";

/** 字段类型 → 输入框。电话 / 地址按单行文字。 */
export function rowEditorKind(type: FieldType | undefined): RowEditorKind {
  switch (type) {
    case "textarea":
      return "textarea";
    case "list":
      return "list";
    case "enum":
      return "enum";
    case "image":
      return "image";
    case "asset_list":
      return "asset_list";
    default:
      return "text";
  }
}

/**
 * 这一行能不能直接改:可采集(不是 AI 起草 / 默认 / 自动同步 / 不公开)、且不是「带默认值的非事实字段」
 * (那种行画「默认:…」,A.25,不进 17 项)。不公开(excluded)的案例不在这里改 —— 那是授权题答出来的,
 * 改它等于绕过「是否公开」的选择。
 */
export function rowEditable(
  item: Pick<ChecklistItem, "status" | "fact"> & { type?: FieldType },
  field: Pick<FieldSpec, "default"> | undefined
): boolean {
  if (!isCollectableRow(item)) return false;
  if (!item.fact && field?.default != null) return false;
  return true;
}

/** 编辑框的初值:列表每项一行,其余原样转字;没有值为空。 */
export function rowInitialText(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) return value.map((v) => String(v ?? "")).join("\n");
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

/**
 * 输入 → 要发的值;空了回 null(调用方不发,api 也会 400「不能为空」)。
 * 列表:按行拆、去空行、同句去重、截到 `max`(卖点 ×3);其余去首尾空白。
 */
export function rowInputValue(
  kind: RowEditorKind,
  raw: string,
  field: Pick<FieldSpec, "max"> | undefined
): string | string[] | null {
  if (kind === "list" || kind === "asset_list") {
    const items: string[] = [];
    for (const line of raw.split(/\r?\n/)) {
      const v = line.trim();
      if (v && !items.includes(v)) items.push(v);
    }
    const capped = field?.max ? items.slice(0, field.max) : items;
    return capped.length ? capped : null;
  }
  const v = raw.trim();
  return v ? v : null;
}

/** 行内编辑的一条答案(A.30 形状,见文件头)。 */
export function rowEditAnswer(path: string, value: unknown): CollectAnswer {
  return { questionId: `${ROW_EDIT_PREFIX}${path}`, fieldPath: path, value, confirm: true };
}

export type ImageUploadTarget = Pick<UploadImageRequest, "purpose" | "productIndex">;

/**
 * 图片行 → 上传端点的用途:Logo / 首屏图 / 产品图各有 purpose;案例配图、资质证书走 `gallery`(W15,1013);
 * 其余回 null。产品图的 pattern 路径(还没有产品行)按第一款算。
 */
export function rowImageTarget(path: string): ImageUploadTarget | null {
  if (path === "brand.logo") return { purpose: "logo" };
  if (path === "pages.home.hero_split.image") return { purpose: "hero" };
  const m = /^products\[(\d*)\]\.image$/.exec(path);
  if (m) return { purpose: "product", productIndex: m[1] ? Number(m[1]) : 0 };
  if (/^cases\[\d*\]\.image$/.test(path) || path === "credentials") return { purpose: "gallery" };
  return null;
}

/** 图片列表行(资质证书等)上传后的新值:追加到已有列表末尾,不把已传的冲掉。 */
export function appendAssetValue(current: unknown, url: string): string[] {
  const list = Array.isArray(current)
    ? current.filter((v): v is string => typeof v === "string")
    : [];
  return list.includes(url) ? list : [...list, url];
}

/** 图片行为什么不能传(null = 能传)。 */
export function rowImageBlocked(path: string, hostMode: boolean): string | null {
  if (!hostMode) return "模拟发布模式没有地方放图,接入宿主后才能上传";
  if (rowImageTarget(path) == null)
    return "这类图片本期还不能在这里上传(上传只收 Logo / 首屏图 / 产品图 / 案例配图 / 资质证书)";
  return null;
}
